import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Loader2, CheckCircle, Mail as MailIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isOlderBrowser } from '../utils/browserDetection';
import { AuthCompat } from './AuthCompat';

type AuthMode = 'signin' | 'signup' | 'reset' | 'confirmation';

export const Auth = () => {
  // Check if this is an older browser
  const [isCompat, setIsCompat] = useState(false);

  useEffect(() => {
    try {
      // Detect if this is an older browser
      const needsCompat = isOlderBrowser();
      setIsCompat(needsCompat);
      
      if (needsCompat) {
        console.log('Using compatibility mode for older browser');
      }
    } catch (err) {
      console.error('Error during browser detection:', err);
      // If detection fails, default to compatibility mode
      setIsCompat(true);
    }
  }, []);

  // Render the compatibility version for older browsers
  if (isCompat) {
    return <AuthCompat />;
  }

  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize/auth mode from URL (e.g., /auth?mode=signup)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const m = params.get('mode');
      if (m === 'signup' || m === 'signin' || m === 'reset') {
        setMode(m as AuthMode);
      }
    } catch (e) {
      // ignore parsing errors
    }
  }, [location.search]);

  const handleResendConfirmation = async () => {
    if (!email || resendCooldown > 0 || resendLoading) return;
    setError(null);
    setMessage(null);
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) throw error;
      setMessage('Te enviamos un nuevo correo de confirmación.');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err?.message || 'No se pudo reenviar el correo de confirmación');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (!resendCooldown) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    
    // Check if terms are accepted when signing up
    if (mode === 'signup' && !acceptedTerms) {
      setError('Debes aceptar los términos y condiciones para continuar');
      return;
    }
    
    setLoading(true);

    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          // Check if the error is about email not being confirmed
          if (error.message?.includes('Email not confirmed')) {
            throw new Error('Email no confirmado. Por favor, revisa tu bandeja de entrada y haz clic en el enlace de confirmación para activar tu cuenta.');
          }
          throw error;
        }

        // After successful sign-in, delegate onboarding decisions to ProtectedRoute
        navigate('/');
      } else if (mode === 'signup') {
        // Capture referral code from URL or localStorage and send as metadata
        let refCode: string | null = null;
        try {
          const params = new URLSearchParams(window.location.search);
          refCode = params.get('ref');
          if (!refCode) {
            try {
              refCode = localStorage.getItem('atlas:ref');
            } catch {}
          } else {
            try {
              localStorage.setItem('atlas:ref', refCode);
            } catch {}
          }
        } catch {}

        const signUpPayload: { email: string; password: string; options?: { data: { ref_code: string } } } = {
          email,
          password,
        };
        if (refCode) {
          signUpPayload.options = { data: { ref_code: refCode } };
        }

        const { data, error } = await supabase.auth.signUp(signUpPayload);
        if (error) throw error;
        
        // Send registration webhook
        try {
          await fetch('https://ai-agency-dt7d.onrender.com/webhook/resgistro_user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
        } catch (webhookError) {
          console.error('Error sending registration webhook:', webhookError);
        }
        
        // If email confirmation is disabled, user is automatically signed in
        // Navigate to home/onboarding
        if (data?.session) {
          navigate('/');
        } else {
          // Fallback to confirmation screen if session not created (email confirmation enabled)
          setMode('confirmation');
        }
      } else if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/restablecer-clave`,
        });
        if (error) throw error;
        setMessage('Si existe una cuenta con este email, recibirás instrucciones para restablecer tu contraseña.');
      }
    } catch (error) {
      if (error instanceof Error) {
        // Check for invalid login credentials error and show Spanish message
        if (error.message === 'Invalid login credentials') {
          setError('Correo electrónico o contraseña incorrectos');
        } else {
          setError(error.message);
        }
      } else {
        setError('Ocurrió un error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reusable Auth Card content (used in mobile and desktop)
  const renderAuthCard = () => (
    <div className="bg-white rounded-none rounded-t-md md:rounded-2xl shadow-lg px-6 md:px-14 py-8 h-full md:flex md:flex-col md:justify-center overflow-auto">
      <h2 className="text-3xl md:text-2xl font-semibold text-gray-900 mb-16 md:mb-2 text-center">
        {mode === 'signin' && 'Iniciar Sesión'}
        {mode === 'signup' && 'Crea tu cuenta y obtené 10 fotos gratis'}
        {mode === 'reset' && 'Restablecer Contraseña'}
        {mode === 'confirmation' && 'Confirma tu Email'}
      </h2>
      <p className="hidden md:block text-gray-500 mb-16 text-center">
        {mode === 'signin' && 'Bienvenido de vuelta a Atlas'}
        {mode === 'signup' && 'Únete a Atlas para empezar'}
        {mode === 'reset' && 'Ingresa tu email para restablecer tu contraseña'}
        {mode === 'confirmation' && 'Hemos enviado un correo de confirmación a tu email'}
      </p>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {message && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
          {message}
        </div>
      )}

      {mode === 'confirmation' ? (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="bg-blue-50 rounded-full p-4 mb-4">
              <MailIcon className="w-10 h-10 text-blue-500" />
            </div>
            <p className="text-center text-gray-600 mb-2">
              Hemos enviado un correo de confirmación a <span className="font-semibold">{email}</span>
            </p>
            <p className="text-center text-gray-500 text-sm">
              Por favor, revisa tu bandeja de entrada y haz clic en el enlace de confirmación para activar tu cuenta.
            </p>
          </div>
          
          <button
            onClick={() => setMode('signin')}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11 px-4 rounded-full shadow-sm font-normal flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Ya lo confirmé
            <CheckCircle className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={handleResendConfirmation}
            disabled={resendCooldown > 0 || resendLoading}
            className="w-full mt-2 text-blue-600 hover:text-blue-700 text-sm bg-transparent border-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0 ? (
              <>
                Reenviar mail
                <br />
                {`(Podés reintentar en ${resendCooldown} seg)`}
              </>
            ) : (
              'Reenviar mail'
            )}
          </button>
        </div>
      ) : (
        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 h-11 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="tu@email.com"
                required
              />
              <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {mode !== 'reset' && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 h-11 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                />
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          )}
          
          {mode === 'signup' && (
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-4 h-4 border border-gray-300 rounded accent-blue-500 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-500">
                  Acepto los{" "}
                  <a 
                    href="https://estudio-atlas.com/politica-de-privacidad" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    términos y condiciones
                  </a>
                </label>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11 px-4 rounded-full shadow-sm font-normal flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {mode === 'signin' && 'Iniciar Sesión'}
                {mode === 'signup' && 'Crear Cuenta'}
                {mode === 'reset' && 'Enviar Instrucciones'}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      )}

      <div className="mt-6 text-center text-sm">
        {mode === 'signin' && (
          <>
            <button
              onClick={() => setMode('reset')}
              className="text-blue-600 hover:text-blue-700 transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </button>
            <div className="mt-2">
              ¿No tienes una cuenta?{' '}
              <button
                onClick={() => setMode('signup')}
                className="text-blue-600 hover:text-blue-700 transition-colors"
              >
                Regístrate
              </button>
            </div>
          </>
        )}
        {mode === 'signup' && (
          <div>
            ¿Ya tienes una cuenta?{' '}
            <button
              onClick={() => setMode('signin')}
              className="text-blue-600 hover:text-blue-700 transition-colors"
            >
              Inicia Sesión
            </button>
          </div>
        )}
        {mode === 'reset' && (
          <button
            onClick={() => setMode('signin')}
            className="text-blue-600 hover:text-blue-700 transition-colors"
          >
            Volver a Iniciar Sesión
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile layout */}
      <div className="md:hidden h-[100dvh] bg-white flex flex-col px-0 pb-0">
        {/* Top: Logo area with fixed spacing */}
        <div className="w-full pt-12 pb-8 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <img src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg" alt="Atlas logo" className="w-16 h-16" />
          </div>
        </div>
        {/* Bottom: Auth Card consumes remaining space */}
        <div className="w-full flex-1">
          {renderAuthCard()}
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:block relative h-screen overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/foto-login.png')" }}
        />

        {/* Foreground content */}
        <div className="relative z-10 flex h-screen">
          {/* Left side (2/3): brand top-left, message bottom-left */}
          <div className="flex-1 flex flex-col justify-between p-8 h-full">
            <div className="flex items-center gap-2">
              <img
                src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/Logo-blanco.svg"
                alt="Atlas logo"
                className="w-8 h-8"
              />
              <span className="text-white font-semibold text-xl" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif' }}>Estudio Atlas</span>
            </div>

            <div className="text-white">
              <div className="text-2xl md:text-3xl font-semibold"> Tu estudio de fotos, sin estudio</div>          
              <div className="text-sm md:text-base mt-2">Modelos virtuales y fondos listos para e-commerce e Instagram.</div>
            </div>
          </div>

          {/* Right side (1/2): auth card */}
          <div className="w-1/2 p-8 flex items-stretch h-screen">
            <div className="w-full ml-auto h-full">
              {renderAuthCard()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};